import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion } from "framer-motion";
import { Calculator, AlertTriangle, ShieldCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const SCENARIO_COSTS: Record<string, number> = {
  unguarded_machinery: 25000,
  non_compliant_scaffolding: 35000,
  hazmat_violation: 30000,
  working_at_height: 20000,
  stop_work_order: 10000,
};

const DAILY_SHUTDOWN_COST = 20000;
const LEGAL_COST = 60000;

export const RiskCalculator = () => {
  const { t } = useTranslation("home");
  const { isRTL } = useLanguage();

  const [scenario, setScenario] = useState<string>("unguarded_machinery");
  const [shutdownDays, setShutdownDays] = useState<number>(5);
  const [includeLegal, setIncludeLegal] = useState<boolean>(false);

  const totalCost = useMemo(() => {
    const base = SCENARIO_COSTS[scenario] || 0;
    const shutdown = shutdownDays * DAILY_SHUTDOWN_COST;
    const legal = includeLegal ? LEGAL_COST : 0;
    return base + shutdown + legal;
  }, [scenario, shutdownDays, includeLegal]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(isRTL ? "he-IL" : "en-IL", {
      style: "currency",
      currency: "ILS",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const scenarios = [
    { value: "unguarded_machinery", label: t("riskCalculator.scenarios.unguarded_machinery") },
    { value: "non_compliant_scaffolding", label: t("riskCalculator.scenarios.non_compliant_scaffolding") },
    { value: "hazmat_violation", label: t("riskCalculator.scenarios.hazmat_violation") },
    { value: "working_at_height", label: t("riskCalculator.scenarios.working_at_height") },
    { value: "stop_work_order", label: t("riskCalculator.scenarios.stop_work_order") },
  ];

  return (
    <section className="py-16 md:py-24 bg-muted/30">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            <Calculator className="w-4 h-4" />
            {t("riskCalculator.badge")}
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
            {t("riskCalculator.title")}{" "}
            <span className="text-primary">{t("riskCalculator.titleAccent")}</span>
          </h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            {t("riskCalculator.subtitle")}
          </p>
        </motion.div>

        {/* Calculator Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl border bg-card shadow-lg p-6 md:p-8 space-y-8"
        >
          {/* Input A: Scenario Dropdown */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">{t("riskCalculator.scenarioLabel")}</Label>
            <Select value={scenario} onValueChange={setScenario}>
              <SelectTrigger className="w-full h-12 text-base bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-popover z-50">
                {scenarios.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {t("riskCalculator.baseCost")}: {formatCurrency(SCENARIO_COSTS[scenario])}
            </p>
          </div>

          {/* Input B: Shutdown Days Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">{t("riskCalculator.shutdownLabel")}</Label>
              <span className="text-lg font-bold text-primary">{shutdownDays}</span>
            </div>
            <Slider
              value={[shutdownDays]}
              onValueChange={([val]) => setShutdownDays(val)}
              min={0}
              max={30}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0</span>
              <span>15</span>
              <span>30</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {t("riskCalculator.shutdownCost")}: {formatCurrency(shutdownDays * DAILY_SHUTDOWN_COST)}
            </p>
          </div>

          {/* Input C: Legal Toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50 border">
            <div className="space-y-0.5">
              <Label className="text-base font-semibold cursor-pointer">{t("riskCalculator.legalLabel")}</Label>
              <p className="text-sm text-muted-foreground">{formatCurrency(LEGAL_COST)}</p>
            </div>
            <Switch checked={includeLegal} onCheckedChange={setIncludeLegal} />
          </div>

          {/* Result: Red Scare Number */}
          <div className="pt-4 border-t">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              <Label className="text-base font-semibold text-destructive">
                {t("riskCalculator.totalLabel")}
              </Label>
            </div>
            <motion.div
              key={totalCost}
              initial={{ scale: 1.1, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="text-4xl md:text-5xl font-bold text-destructive"
              dir="ltr"
            >
              {formatCurrency(totalCost)}
            </motion.div>
          </div>

          {/* CTA: Green Solution Box */}
          <div className="rounded-xl bg-accent/10 border border-accent/30 p-5 space-y-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-6 h-6 text-accent mt-0.5 shrink-0" />
              <p className="text-foreground font-medium leading-relaxed">
                {t("riskCalculator.ctaText")}
              </p>
            </div>
            <Button asChild variant="cta" className="w-full">
              <Link to="/contact">
                {t("riskCalculator.ctaButton")}
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
